import React, { useMemo } from "react";
import { FormGroup, Input } from "reactstrap";
import useResourceLoader from '../../util/useResourceLoader';
import fetchApi from "../../util/fetchApi";
import checkErrors from "../../api/checkErrors";
import Notification from "../Notification";
import Loader from "../Loader";
import { useSideChannelSubscription } from "../../util/useSideChannel";
import { getObject } from "../../util/mapObject";
import { compileShowIf, evalShowIf, getPathFromId } from "./HideObjectField";


/**
 * Renders a lookup dropdown when the given condition is true,
 * or a plain text input otherwise. Both modes write to the same field.
 *
 * Schema: include a `lookup` object (api, params, id, label, resource).
 * uiSchema: include `"akc:condition": { "expr": "<jnx expression>" }`.
 */
function ConditionalLookupFormField(props) {
    const {
        formData,
        title: propTitle,
        formContext,
        formContext: { sideChannel },
        disabled,
        schema: {
            title: schemaTitle,
            lookup,
            type
        },
        uiSchema: {
            'akc:requiredIfVisible': akcRequiredIfVisible,
            'ui:readonly': readonly,
            'akc:condition': condition,
        },
        idSchema: { $id },
        required: requiredProp,
        onChange: propOnChange
    } = props;

    const title = schemaTitle || propTitle;

    const required = requiredProp || akcRequiredIfVisible;

    const {
        resource = "Options",
        api,
        params,
        id,
        label,
        noValue = ''
    } = lookup || {};

    const [rootFormData, formObject] = useSideChannelSubscription(sideChannel, [0, 1]) || [{}];

    const route = useMemo(() => getPathFromId($id), [$id]);

    const bindings = useMemo(() => ({
        formContext,
        rootFormData,
        formObject
    }), [formContext, rootFormData, formObject]);

    const useLookup = useMemo(() => {
        if (!condition) return !!api;
        const jnx = compileShowIf(condition);
        return evalShowIf(rootFormData, route, jnx, bindings);
    }, [condition, rootFormData, route, bindings, api]);

    const paramsString = useMemo(() => (
        rootFormData && params
            ? Object.entries(params).map(([k, v]) => {
                if (v && v.field) v = getObject(rootFormData, v.field);
                return v ? `${k}=${encodeURIComponent(v)}` : null;
            }).filter(s => !!s).join("&")
            : ""
    ), [rootFormData, params]);

    const [options, loadingOptions, errorLoadingOptions] = useResourceLoader(
        () => useLookup && api
            ? fetchApi.lookup.get(`${api}${paramsString ? `?${paramsString}` : ''}`).then(checkErrors)
            : Promise.resolve(null),
        [useLookup, api, paramsString]
    );

    function onSelectChange({ target: { value } }) {
        const parsed = type === "number" ? (value | 0) : value;
        propOnChange(parsed || undefined);
    }

    function onTextChange({ target: { value } }) {
        propOnChange(value || undefined);
    }

    return (
        <FormGroup disabled={readonly || disabled}>
            <label className="control-label">
                {title}{required ? <span className="required">*</span> : null}
            </label>
            {useLookup ? (
                loadingOptions ? (
                    <div><Loader>Loading {resource}</Loader></div>
                ) : errorLoadingOptions ? (
                    <Notification color="danger">
                        <div>{errorLoadingOptions.message}</div>
                    </Notification>
                ) : (
                    <Input
                        type="select"
                        disabled={readonly || disabled}
                        required={required}
                        value={formData || ''}
                        onChange={onSelectChange}
                    >
                        <option value="">{noValue}</option>
                        {(options || []).map((option, idx) => (
                            <option key={idx} value={option[id]}>{option[label]}</option>
                        ))}
                    </Input>
                )
            ) : (
                <Input
                    type="text"
                    disabled={readonly || disabled}
                    required={required}
                    value={formData || ''}
                    onChange={onTextChange}
                />
            )}
        </FormGroup>
    );
}


export default ConditionalLookupFormField;